import { useState, useEffect } from "react";
import { User, ArrowRightLeft } from "lucide-react";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { authenticatedFetch } from "@/lib/auth";

interface SupportUser {
    id: number;
    full_name: string;
    email: string;
    role_name: string;
}

interface Ticket {
    id: number;
    ticket_number: string;
    assigned_to?: number;
    assigned_to_name?: string;
}

interface AssignTicketModalProps {
    isOpen: boolean;
    onClose: () => void;
    ticket: Ticket;
    mode: 'assign' | 'transfer';
    onSuccess: () => void;
}

export const AssignTicketModal = ({
    isOpen,
    onClose,
    ticket,
    mode,
    onSuccess,
}: AssignTicketModalProps) => {
    const [supportUsers, setSupportUsers] = useState<SupportUser[]>([]);
    const [selectedUserId, setSelectedUserId] = useState<string>("");
    const [motivoTransferencia, setMotivoTransferencia] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingUsers, setIsLoadingUsers] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Carregar lista de usuários de suporte
    useEffect(() => {
        if (isOpen) {
            loadSupportUsers();
            resetForm();
        }
    }, [isOpen]);

    const loadSupportUsers = async () => {
        setIsLoadingUsers(true);
        try {
            const response = await authenticatedFetch("/api/admin/users.php?user_type=internal");
            
            if (!response.ok) {
                throw new Error("Erro ao carregar usuários");
            }

            const result = await response.json();
            
            // API retorna { data: [...], pagination: {...} }
            if (result.data && Array.isArray(result.data)) {
                // Filtrar apenas admin e support
                const filtered = result.data.filter(
                    (user: any) => user.role_id === 1 || user.role_id === 2 || user.role_id === 4
                );
                
                // Se modo transfer, filtrar o usuário atual
                const finalList = mode === 'transfer' 
                    ? filtered.filter((user: any) => user.id !== ticket.assigned_to)
                    : filtered;
                
                setSupportUsers(finalList);
            }
        } catch (err) {
            console.error("Erro ao carregar usuários:", err);
            setError("Erro ao carregar lista de atendentes");
        } finally {
            setIsLoadingUsers(false);
        }
    };

    const resetForm = () => {
        setSelectedUserId("");
        setMotivoTransferencia("");
        setError(null);
    };

    const validateForm = (): boolean => {
        setError(null);

        if (!selectedUserId) {
            setError("Selecione um atendente");
            return false;
        }

        if (mode === 'transfer') {
            if (!motivoTransferencia.trim()) {
                setError("Motivo da transferência é obrigatório");
                return false;
            }
            if (motivoTransferencia.trim().length < 10) {
                setError("Motivo deve ter no mínimo 10 caracteres");
                return false;
            }
        }

        return true;
    };

    const handleSubmit = async () => {
        if (!validateForm()) {
            return;
        }

        setIsLoading(true);
        setError(null);

        try {
            const payload: any = {
                type: 'assignment',
                message: mode === 'transfer' 
                    ? `Ticket transferido. Motivo: ${motivoTransferencia.trim()}`
                    : 'Ticket atribuído',
                metadata: {
                    assigned_to: parseInt(selectedUserId),
                    assigned_to_name: supportUsers.find(u => u.id === parseInt(selectedUserId))?.full_name || '',
                    action: mode === 'transfer' ? 'transfer' : 'assign',
                }
            };

            if (mode === 'transfer') {
                payload.metadata.transfer_reason = motivoTransferencia.trim();
                payload.metadata.previous_assigned_to = ticket.assigned_to;
                payload.metadata.previous_assigned_to_name = ticket.assigned_to_name;
            }

            const response = await authenticatedFetch(`/api/tickets/${ticket.id}/interact`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload),
            });

            const result = await response.json();

            if (!response.ok) {
                throw new Error(result.error || `Erro ao ${mode === 'transfer' ? 'transferir' : 'atribuir'} ticket`);
            }

            onSuccess();
            onClose();
            resetForm();
        } catch (err) {
            console.error(`Erro ao ${mode === 'transfer' ? 'transferir' : 'atribuir'} ticket:`, err);
            setError(err instanceof Error ? err.message : `Erro ao ${mode === 'transfer' ? 'transferir' : 'atribuir'} ticket`);
        } finally {
            setIsLoading(false);
        }
    };

    const getTitle = () => {
        return mode === 'transfer' ? 'Transferir Ticket' : 'Atribuir Ticket';
    };

    const getDescription = () => {
        if (mode === 'transfer') {
            return (
                <>
                    Transferir ticket <strong>{ticket.ticket_number}</strong> de{' '}
                    <strong>{ticket.assigned_to_name || 'Não atribuído'}</strong> para outro atendente
                </>
            );
        }
        return (
            <>
                Selecione o atendente para o ticket <strong>{ticket.ticket_number}</strong>
            </>
        );
    };

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="sm:max-w-[500px]">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        {mode === 'transfer' ? (
                            <ArrowRightLeft className="w-5 h-5" />
                        ) : (
                            <User className="w-5 h-5" />
                        )}
                        {getTitle()}
                    </DialogTitle>
                    <DialogDescription>
                        {getDescription()}
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4 py-4">
                    {/* Exibir usuário atual em modo transfer */}
                    {mode === 'transfer' && ticket.assigned_to_name && (
                        <Alert>
                            <AlertDescription>
                                <strong>Atendente atual:</strong> {ticket.assigned_to_name}
                            </AlertDescription>
                        </Alert>
                    )}

                    {/* Seleção de atendente */}
                    <div className="space-y-2">
                        <Label htmlFor="assigned-user">
                            {mode === 'transfer' ? 'Novo Atendente' : 'Atendente'}
                        </Label>
                        {isLoadingUsers ? (
                            <div className="h-10 bg-gray-100 rounded animate-pulse" />
                        ) : (
                            <Select
                                value={selectedUserId}
                                onValueChange={setSelectedUserId}
                                disabled={isLoading}
                            >
                                <SelectTrigger id="assigned-user">
                                    <SelectValue placeholder="Selecione um atendente" />
                                </SelectTrigger>
                                <SelectContent>
                                    {supportUsers.map((user) => (
                                        <SelectItem
                                            key={user.id}
                                            value={user.id.toString()}
                                        >
                                            {user.full_name} - {user.role_name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        )}
                    </div>

                    {/* Campo de motivo (apenas em modo transfer) */}
                    {mode === 'transfer' && (
                        <div className="space-y-2">
                            <Label htmlFor="motivo">
                                Motivo da Transferência <span className="text-red-500">*</span>
                            </Label>
                            <Textarea
                                id="motivo"
                                placeholder="Explique o motivo da transferência (mínimo 10 caracteres)"
                                value={motivoTransferencia}
                                onChange={(e) => setMotivoTransferencia(e.target.value)}
                                disabled={isLoading}
                                rows={4}
                                className="resize-none"
                            />
                            <p className="text-xs text-gray-500">
                                {motivoTransferencia.length}/10 caracteres mínimos
                            </p>
                        </div>
                    )}

                    {/* Mensagem de erro */}
                    {error && (
                        <Alert variant="destructive">
                            <AlertDescription>{error}</AlertDescription>
                        </Alert>
                    )}

                    {/* Aviso se não há atendentes */}
                    {supportUsers.length === 0 && !isLoadingUsers && (
                        <Alert>
                            <AlertDescription>
                                {mode === 'transfer' 
                                    ? 'Nenhum outro atendente disponível para transferência.'
                                    : 'Nenhum atendente disponível no momento.'}
                            </AlertDescription>
                        </Alert>
                    )}
                </div>

                <DialogFooter>
                    <Button
                        variant="outline"
                        onClick={() => {
                            onClose();
                            resetForm();
                        }}
                        disabled={isLoading}
                    >
                        Cancelar
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        disabled={!selectedUserId || isLoading || supportUsers.length === 0}
                    >
                        {isLoading 
                            ? (mode === 'transfer' ? 'Transferindo...' : 'Atribuindo...') 
                            : (mode === 'transfer' ? 'Transferir' : 'Atribuir')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
